import { TimelineEntryKind } from "types/graphql-schema";

import { ComplianceProgramProgression } from "@apps/programs/components/compliance-program-progression";
import { conditionalTimelineMessage } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import Loading from "@components/loading/loading";

import ComplianceProgramFormReminders from "../compliance-program-form-reminders";
import { ProgramForm } from "../types";
import useProgramFormTimeline, {
  timelineGapMessage,
} from "../use-program-form-timeline";

/**
 * The Notifications tab: what the admin adds to the review, then what the review
 * sends on its own.
 *
 * The admin's reminders come first. The automatic list is long (a dozen rows on
 * a normal review) and filled the screen, so admins who had to scroll past it
 * never found that they could schedule reminders of their own.
 *
 * The tab used to hold the reminder table alone. An admin opening it to ask "which
 * notifications will this send, and when?" found an empty reminder list and
 * concluded the review sends nothing, when in fact it sends up to nine messages
 * nobody schedules. The answer under the reminders is the Timeline's own
 * notification rows, so it can never fall out of step with what is really sent:
 * there is no second list of notifications written down anywhere.
 *
 * Opening the tab is the question, so the automatic list loads on its own: a list
 * hidden behind a "show" button to press first is the empty tab again.
 *
 * Only the messages sent at kickoff really go out on a date. The rest go out as
 * people act, such as a manager review unlocking as each self review comes in, so
 * they read in order: kickoff, then what people set off, then the due dates.
 */
const shownKinds = [
  TimelineEntryKind.Kickoff,
  TimelineEntryKind.StepOpens,
  TimelineEntryKind.StepDue,
  TimelineEntryKind.Notification,
];

const ProgramFormNotifications = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
}: {
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: Partial<ProgramForm>) => void;
  canUpdate: boolean;
}) => {
  const { noun } = useProgramsSection();
  const { rows, loading, error, refusal, gap, conditionalKickoffAfterDays } =
    useProgramFormTimeline(proposedComplianceProgram);

  // The automatic messages and the calendar they are drawn against, and only
  // while the plan describes the form as it is now. The admin's own reminders are
  // rows of the plan too, but they are edited in the table above and listing them
  // twice on one screen reads as two reminders. A plan with no message at all
  // draws nothing, so the card says the review sends nothing rather than showing
  // a calendar that reads as if it did.
  const plan = (gap === null ? (rows ?? []) : []).filter((row) =>
    shownKinds.includes(row.kind),
  );
  const automatic = plan.some(
    (row) => row.kind === TimelineEntryKind.Notification,
  )
    ? plan
    : [];

  return (
    <>
      <ComplianceProgramFormReminders
        proposedComplianceProgram={proposedComplianceProgram}
        onUpdateProposedComplianceProgram={onUpdateProposedComplianceProgram}
        canUpdate={canUpdate}
      />

      <div className="mt-12 flex items-center justify-between">
        <div className="text-lg font-semibold">Automatic Notifications</div>
        {gap === null && loading && rows && <Loading size="4" mini />}
      </div>
      <div className="mt-1 text-sm text-gray-500">
        {`This ${noun.toLowerCase()} sends notifications at specific dates, and automatically as certain events are completed.`}
      </div>

      {/* A conditional review has no dates of its own, so the list below is dated
          from today. The same two sentences as the Workflow tab's panel, because
          the two are filtered views of one plan. */}
      {conditionalKickoffAfterDays !== null && (
        <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {conditionalTimelineMessage(
            noun.toLowerCase(),
            conditionalKickoffAfterDays,
          ).map((sentence) => (
            <p key={sentence} className="mt-1 first:mt-0">
              {sentence}
            </p>
          ))}
        </div>
      )}

      {/* The backend's own words come through when it has any: a rule reaching
          outside the admin's tree is refused with a sentence saying what to
          change, which "reload the page" would bury. */}
      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
          {refusal ||
            (rows
              ? "This list could not be refreshed, so it may be out of date. Reload the page to try again."
              : "These notifications could not be loaded. Reload the page to try again.")}
        </div>
      )}

      <div className="mt-3">
        <ComplianceProgramProgression
          rows={automatic}
          empty={
            gap !== null ? (
              timelineGapMessage(gap, noun.toLowerCase())
            ) : loading ? (
              <Loading size="5" />
            ) : // The banner above already says what went wrong.
            error ? null : (
              `This ${noun.toLowerCase()} sends nothing on its own. Every step it has is one nobody is notified about.`
            )
          }
        />
      </div>
    </>
  );
};

export default ProgramFormNotifications;
